<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import { hasProductAccess } from '@/Utils/teamPermissionRows'
import { sitesUpgradeCopy, sitesPlanLine, COUNTS_TOWARD_LIMIT } from '@/Utils/sitesPlanLimits'
import { siteAddress } from '@/Utils/siteUrl'
import { trackSites, SITES_EVENTS, SITES_UPGRADE_REASONS } from '@/lib/tracking/sites'
import Spinner from '@/components/ui/Spinner.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import NoAccessState from '@/components/ui/NoAccessState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import AddSiteModal from '@/components/modals/AddSiteModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import SiteHostStatusPill from '@/components/Sites/SiteHostStatusPill.vue'
import SitesUpgradeModal from '@/components/Sites/SitesUpgradeModal.vue'
import { LOCKED_TITLE } from '@/composables/useSiteHostReadiness'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconGlobe from '@/components/icon/IconGlobe.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconLock from '@/components/icon/IconLock.vue'
import IconGear from '@/components/icon/IconGear.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconLinkExternal from '@/components/icon/IconLinkExternal.vue'
import IconElipsisVertical from '@/components/icon/IconElipsisVertical.vue'

const store = useStore()
const router = useRouter()
const { notify } = useNotification()

const addSiteModal = ref(null)
const loading = ref(true)
const selectedSiteId = ref(null)

// Plan + team gates: same pattern as Paywall (definition flag / cap on the live
// package, TeamMemberPermission.sites for members).
const activeTeam = computed(() => store.state.User.activatedTeam)
const userPermissions = computed(() => store.getters['User/userPermissions'])
const canAccessSites = computed(() => !activeTeam.value || hasProductAccess(userPermissions.value, 'sites'))
const ownerEmail = computed(() => store.getters['User/teamOwner']?.email || '')
const hasSitesFeature = computed(() => store.getters['User/hasSitesFeature'])
const maxSites = computed(() => store.getters['User/maxSites'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired'])

const sites = computed(() => store.getters['Sites/sites'])
const caps = computed(() => store.getters['Sites/caps'])
const capsLoaded = computed(() => store.getters['Sites/capsLoaded'])
const teamPermissionError = computed(() => store.getters['Sites/teamPermissionError'])

// The backend caps are authoritative (they include definitionOverride); the
// User getters only decide what to show before the first /sites response.
const sitesEnabled = computed(() => caps.value?.enabled ?? (hasSitesFeature.value && maxSites.value > 0))
const sitesLimit = computed(() => {
	const limit = caps.value?.maxSites || maxSites.value
	return typeof limit === 'number' && limit > 0 ? limit : null
})
// The cap is the account's while the list is the active workspace's, so the
// plan side always counts castr's `used` (drafts and disabled sites included).
const planUsed = computed(() => {
	const used = capsLoaded.value ? Number(caps.value?.used) : NaN
	return Number.isFinite(used) ? Math.max(used, sites.value.length) : sites.value.length
})
const atSitesLimit = computed(() => sitesLimit.value !== null && planUsed.value >= sitesLimit.value)
const isCreateDisabled = computed(() => !hasLiveSubscription.value || isLiveSubscriptionExpired.value)

// A plan limit keeps "New Site" clickable (with a lock) and opens the upgrade popup instead.
const createBlockedBy = computed(() => {
	if (!sitesEnabled.value) return 'not-in-plan'
	if (atSitesLimit.value) return 'limit'
	return null
})
const upgradeModalVisible = ref(false)
// set when castr refused a create the page thought was allowed (stale caps)
const createRefusal = ref(null)
const upgradeCopy = computed(() => sitesUpgradeCopy(createRefusal.value || createBlockedBy.value, { limit: sitesLimit.value }))
watch(upgradeModalVisible, (open) => { if (!open) createRefusal.value = null })
const upgradeReason = computed(() => ((createRefusal.value || createBlockedBy.value) === 'limit'
	? SITES_UPGRADE_REASONS.SITES_LIMIT_REACHED
	: SITES_UPGRADE_REASONS.SITES_NOT_IN_PLAN))
const upgradeCounts = computed(() => ({ sites_used: planUsed.value, max_sites: sitesLimit.value ?? 0 }))

const headerUsage = computed(() => {
	if (!planUsed.value) return null
	const meta = { used: planUsed.value, label: 'sites', labelSingular: 'site' }
	if (sitesLimit.value) {
		meta.limit = sitesLimit.value
		const here = planUsed.value !== sites.value.length ? ` (${sites.value.length} in this workspace)` : ''
		meta.tooltip = `Using ${planUsed.value} of ${sitesLimit.value} sites on your plan${here}. ${COUNTS_TOWARD_LIMIT}`
	}
	return meta
})
const planLine = computed(() => (capsLoaded.value && sitesEnabled.value ? sitesPlanLine(caps.value) : ''))

// The status pill (`SiteHostStatusPill`) says "Setting up address" while the
// App Platform edge is still routing a new Castr address; the row's "Open
// site" waits for the same answer (castr `hostReadiness.ready === false`).
const addressPending = (site) => site.hostReadiness?.ready === false
const canOpen = (site) => site.status === 'active' && !addressPending(site)
const openTitle = (site) => (addressPending(site) ? LOCKED_TITLE : 'Open site')

onMounted(async () => {
	loading.value = true
	try {
		await store.dispatch('Sites/getSites')
		store.dispatch('Sites/setTeamPermissionError', { error: false })
	} catch (err) {
		if (err && String(err.message || err).includes('not have access to')) {
			store.dispatch('Sites/setTeamPermissionError', { error: true })
		} else {
			notify({ group: 'error', text: err.message || 'Could not load your sites.' })
		}
	}
	loading.value = false
	if (canAccessSites.value && !teamPermissionError.value && !sitesEnabled.value && sites.value.length === 0) {
		trackSites(SITES_EVENTS.UPGRADE_PROMPT_SHOWN, null, { reason: SITES_UPGRADE_REASONS.SITES_NOT_IN_PLAN, surface: 'empty_state', ...upgradeCounts.value })
	}
})

const showCreateSiteModal = () => {
	if (createBlockedBy.value) {
		upgradeModalVisible.value = true
		return
	}
	addSiteModal.value?.show()
}

const openSite = (site) => {
	if (!canOpen(site)) return
	const url = siteAddress(site)
	if (url) window.open(url, '_blank', 'noopener')
}

const onCreateRefused = ({ kind }) => {
	createRefusal.value = kind
	upgradeModalVisible.value = true
	store.dispatch('Sites/loadCaps').catch(() => null)
}

const onSiteCreated = (site) => {
	router.push({ name: 'SiteManage', params: { siteId: site.id } })
}

const onDeleteSiteRequest = (site) => {
	selectedSiteId.value = site.id
	showModal('delete-site')
}

const onSiteDelete = async () => {
	if (!selectedSiteId.value) return
	const site = store.getters['Sites/siteById'](selectedSiteId.value)
	try {
		await store.dispatch('Sites/deleteSite', selectedSiteId.value)
		trackSites(SITES_EVENTS.SITE_DELETED, site || selectedSiteId.value)
		notify({ group: 'success', text: `Site "${site?.name || ''}" deleted.` })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Unable to delete the site.' })
	}
	selectedSiteId.value = null
}
</script>

<template>
	<div class="container detail-shell px-4">
		<Spinner v-if="loading" text="Loading your sites…" classes="text-surface-8 mt-96" spinner-color="var(--c-dark-8)"
			spinner-size="15px" />

		<main v-else-if="!canAccessSites || teamPermissionError">
			<PageHeader title="Sites" subtitle="Launch a branded streaming site for your live and on-demand content." />
			<NoAccessState :owner-email="ownerEmail" />
		</main>

		<main v-else-if="!sitesEnabled && sites.length === 0">
			<PageHeader title="Sites" subtitle="Launch a branded streaming site for your live and on-demand content." />
			<EmptyState title="Upgrade to unlock Sites"
				subtitle="Publish your own OTT site on a Castr address, with your branding and your paywall. It's not included in your current plan.">
				<template #icon>
					<icon-base :width="28" :height="28" :width-view-box="16" :height-view-box="16">
						<icon-globe />
					</icon-base>
				</template>
				<template #actions>
					<FormButton to="/subscribe" class="w-64 justify-center">
						Upgrade Plan
					</FormButton>
				</template>
			</EmptyState>
		</main>

		<main v-else-if="sites.length === 0">
			<PageHeader mobile-inline title="Sites"
				subtitle="Launch a branded streaming site for your live and on-demand content." :usage="headerUsage">
				<template v-if="planLine" #meta>
					<span class="text-tiny text-surface-7" data-testid="sites-plan-line">Plan: {{ planLine }}</span>
				</template>
				<FormButton size="md" class="shrink-0" :disabled="isCreateDisabled" data-testid="new-site" @click="showCreateSiteModal">
					<icon-base v-if="createBlockedBy" class="mr-1.5" :width="14" :height="14" fill="none">
						<icon-lock />
					</icon-base>
					<icon-base v-else class="mr-1.5" :width="14" :height="14">
						<icon-plus />
					</icon-base>
					New Site
				</FormButton>
			</PageHeader>
			<EmptyState title="Create your first site"
				subtitle="Pick a name, get a Castr address instantly, and start publishing. You can connect your own domain later.">
				<template #icon>
					<icon-base :width="28" :height="28" :width-view-box="16" :height-view-box="16">
						<icon-globe />
					</icon-base>
				</template>
				<template #actions>
					<FormButton class="gap-1 w-64 justify-center" :disabled="isCreateDisabled" @click="showCreateSiteModal">
						<icon-base v-if="createBlockedBy" class="mr-2" fill="none">
							<icon-lock />
						</icon-base>
						<icon-base v-else class="mr-2">
							<icon-plus />
						</icon-base>
						New Site
					</FormButton>
				</template>
			</EmptyState>
		</main>

		<main v-else>
			<PageHeader mobile-inline title="Sites"
				subtitle="Launch a branded streaming site for your live and on-demand content." :usage="headerUsage">
				<template v-if="planLine" #meta>
					<span class="text-tiny text-surface-7" data-testid="sites-plan-line">Plan: {{ planLine }}</span>
				</template>
				<FormButton class="gap-1 shrink-0" size="md" :disabled="isCreateDisabled" data-testid="new-site"
					@click="showCreateSiteModal">
					<icon-base v-if="createBlockedBy" class="mr-1.5" :width="14" :height="14" fill="none">
						<icon-lock />
					</icon-base>
					<icon-base v-else class="mr-1.5" :width="14" :height="14">
						<icon-plus />
					</icon-base>
					New <span class="hidden md:block">Site</span>
				</FormButton>
			</PageHeader>

			<section class="mt-4">
				<ul class="flex flex-col gap-2.5 list-none m-0 p-0">
					<li v-for="site in sites" :key="site.id" class="rounded-lg bg-surface-2">
						<div class="flex items-center gap-3 px-3.5 py-3">
							<router-link class="flex min-w-0 flex-1 items-center gap-3 text-inherit hover:no-underline"
								:to="{ name: 'SiteManage', params: { siteId: site.id } }">
								<span
									class="grid size-9 shrink-0 place-items-center rounded-lg bg-surface-3 text-surface-8">
									<icon-base :width="18" :height="18">
										<icon-globe />
									</icon-base>
								</span>
								<div class="min-w-0 flex-1">
									<h2 class="mb-0.5 flex items-center gap-2 truncate text-sm font-medium text-white"
										:title="site.name">
										<span class="truncate">{{ site.name }}</span>
										<SiteHostStatusPill :site="site" poll />
										<StatusPill v-if="site.publish?.status === 'failed'" pill size="sm" variant="warning">
											Publish failed
										</StatusPill>
									</h2>
									<p class="mb-0 truncate text-tiny text-surface-7">
										{{ site.canonicalHost || site.primaryHost }}<span v-if="addressPending(site)" class="text-surface-6"> · not reachable yet</span>
									</p>
								</div>
							</router-link>

							<!-- Mobile: kebab menu -->
							<DropDownMenu is-icon position="right" type="secondary" no-border class="shrink-0 md:hidden">
								<template #toggle-button>
									<icon-base :width="16" :height="16">
										<icon-elipsis-vertical />
									</icon-base>
								</template>
								<template #menu-items>
									<DropDownMenuItem :disabled="!canOpen(site)" :title="openTitle(site)" @click="openSite(site)">
										Open site
									</DropDownMenuItem>
									<DropDownMenuItem @click="router.push({ name: 'SiteManage', params: { siteId: site.id } })">
										Settings
									</DropDownMenuItem>
									<DropDownMenuItem @click="onDeleteSiteRequest(site)">Delete</DropDownMenuItem>
								</template>
							</DropDownMenu>

							<!-- Desktop: inline actions -->
							<div class="hidden shrink-0 items-center gap-1 md:flex">
								<FormButton is-icon type="secondary" size="sm" :title="openTitle(site)" data-testid="site-row-open"
									:disabled="!canOpen(site)" @click="openSite(site)">
									<icon-base :width="16" :height="16" fill="none">
										<icon-link-external />
									</icon-base>
								</FormButton>
								<FormButton is-icon type="secondary" size="sm" title="Settings"
									:to="{ name: 'SiteManage', params: { siteId: site.id } }">
									<icon-base :width="16" :height="16" fill="none">
										<icon-gear />
									</icon-base>
								</FormButton>
								<FormButton is-icon type="danger" size="sm" title="Delete" @click="onDeleteSiteRequest(site)">
									<icon-base :width="16" :height="16" fill="none">
										<icon-bin />
									</icon-base>
								</FormButton>
							</div>
						</div>
					</li>
				</ul>
			</section>
		</main>

		<AddSiteModal ref="addSiteModal" @site-created="onSiteCreated" @plan-refused="onCreateRefused" />
		<SitesUpgradeModal v-model:is-visible="upgradeModalVisible" :title="upgradeCopy.title" :body="upgradeCopy.body"
			:reason="upgradeReason" :counts="upgradeCounts" />
		<ConfirmModal modal-id="delete-site" modal-type="danger" ok-text="Delete site" cancel-text="Cancel"
			title="Delete this site?"
			message="The site goes offline immediately and its address is released. This cannot be undone."
			@modal-confirm="onSiteDelete" />
	</div>
</template>
